// @flow
/* eslint-disable no-fallthrough  */
/* eslint-disable no-case-declarations */
import type { State, BaseAction, ThunkAction, Dispatch } from 'Common/types';
import { isEmpty } from 'lodash';
import type { Store } from 'redux';
import {
  isCastSessionActive,
  optimizeSource,
  generateSendStateCastMessage,
  getSlingConfig,
  getCastDetails,
} from 'Common/cast/CastUtils';
import { CastMethodsModule } from 'Common/cast/CastMethodsModule';
import {
  setPlayingOnCastDevice,
  setStoreHasBeenSynced,
  setCastSourceType,
} from 'Common/cast/actions';
import { getProgressFromRecents, resetPlayer } from 'Common/player/actions';
import { fetchAllNetworks } from 'Common/networks/actions';
import { SourceType, isSlingSourceType } from 'Common/models/franchise';
import type { CastDetails } from 'Common/models/types';
import type { CastConfig, CastSenderToReceiverMessageType, CastType } from 'Common/cast/types';

const getRecordingByDvrId = (dvrId, state) => {
  const {
    dvr: {
      recordings: { content: recordings },
      recordingsDetails: { content: recordingsDetails },
    },
  } = state;

  const recording = recordingsDetails.find(c => c.pgm_id === dvrId);
  if (recording) {
    const slug = recordings.find(r => r.series_id === recording.series_id)?.slug;
    recording.slug = slug || '';
  }
  return recording;
};

const createCastAsset = ({
  castDetails,
  sourceType = 'none',
  serializedStream = '',
  seekTo,
}): CastConfig => ({
  title: castDetails?.title || '',
  subtitle: castDetails?.subtitle || '',
  sourceType,
  poster: castDetails?.poster || '',
  serializedStream,
  seekTo: typeof seekTo === 'number' ? seekTo : 0,
});

const startCast = ({
  state,
  dispatch,
  sourceType,
  serializedStream,
  castDetails,
  seekTo,
  syncFullStore,
  castType,
}: {
  state: State,
  dispatch: Dispatch,
  sourceType: SourceType,
  serializedStream: string,
  syncFullStore: boolean,
  castDetails?: CastDetails,
  seekTo: ?number,
  castType: CastType,
}) => {
  /* It is necessary to fully sync store only one time, all subsequent "casts" can be done with "partial" syncing. */
  const setStateMessage: CastSenderToReceiverMessageType = generateSendStateCastMessage(
    state,
    syncFullStore ? undefined : ['franchise', 'app', 'config', 'profiles'],
  );

  console.log('[CAST] Sending store: ', setStateMessage);

  /* Syncing state */
  CastMethodsModule.sendCustomMessage(JSON.stringify(setStateMessage))
    .then(() => {
      console.log('[CAST] Successfully sent store.');
      if (syncFullStore) dispatch(setStoreHasBeenSynced(true));
    })
    .catch(err =>
      console.error('[CAST] Fail to send store: ', err, '. Trying to sent LOAD request anyway.'),
    )
    .finally(() => {
      const asset: CastConfig = createCastAsset({
        castDetails,
        sourceType,
        serializedStream,
        seekTo,
      });

      if (castType === 'SEND_LOAD_MEDIA_REQUEST') {
        /* Loading asset */
        CastMethodsModule.cast(JSON.stringify(asset))
          .then(() => dispatch(setPlayingOnCastDevice(true)))
          .catch(errCode => {
            dispatch(setPlayingOnCastDevice(false, errCode));
            console.error(`[CAST] Fail to cast: `, errCode);
          });
      } else if (castType === 'SEND_AUSTIN_CONFIGURE_REQUEST') {
        const austinAsset: CastSenderToReceiverMessageType = {
          type: 'AUSTIN_CONFIGURE',
          payload: { ...asset },
        };
        /* Send request to tune austin to another channel / dvr_id */
        CastMethodsModule.sendCustomMessage(JSON.stringify(austinAsset))
          .then(() => dispatch(setPlayingOnCastDevice(true)))
          .catch(errCode => {
            dispatch(setPlayingOnCastDevice(false, errCode));
            console.error(`[CAST] Fail to cast: `, errCode);
          });
      }
    });
};

const getCurrentContentTime = async (
  dispatch: Dispatch,
  franchiseInfo: Object,
  stream: Object,
): any => {
  const episode = franchiseInfo.episodes
    ? franchiseInfo.episodes.find(
        episode => episode && stream && episode.video_id === stream.video_id,
      )
    : undefined;

  const { content_identification: { content_id: franchiseContentId } = {} } = franchiseInfo;
  const contentId = episode ? episode.content_identification.content_id : franchiseContentId;

  const progress = await dispatch(getProgressFromRecents(contentId));
  return progress;
};

export default (store: Store<State, BaseAction | ThunkAction, Dispatch>) => (
  next: any => any,
) => async (action: BaseAction) => {
  let dvrId = -1;
  let stream = {};
  switch (action.type) {
    case 'SET_PLAYER_STREAM':
      const { stream: s } = action;
      stream = s;
    case 'SET_DVR_STREAM':
      // $FlowExpectedError
      const { dvrId: id } = action;
      dvrId = id;
    case 'SET_LIVE_STREAM': {
      const { getState, dispatch } = store;
      const state = getState();

      if (isCastSessionActive(state.cast.sessionState)) {
        // action = JSON.parse(
        //   `{"type":"SET_PLAYER_STREAM","stream":{"title":"Tasting Is Believing","castDetails":{"title":"Food: Fact or Fiction?","subtitle":"Tasting Is Believing","poster":"https://img.dishanywhere.com/dish/61/ae/61aeaa149681a2942cabd013150c07c0.jpg"},"drm":{"fairplay":"https://p-drmfp.movetv.com/fairplay","widevine":"https://p-drmwv.movetv.com/widevine/proxy"},"qvtUrl":"http://8fcacae2.cdn.cms.movetv.com/cms/api/channels/2377d054e80f4565a40f9496956ce549/schedule/99bf344010fd44cbbc26fa995db9a6ea/playback_info.qvt","source":{"dash":"http://p-cdn1-802-cg14-linear-cbd46b77.movetv.com/clipslist/10/20200623T133000Z/20200623T140000Z.mpd","hls":"http://p-cdn1-802-cg14-linear-cbd46b77.movetv.com/clipslist/10/20200623T133000Z/20200623T140000Z/master_7_3_fairplay.m3u8"},"hlsGuid":"3a1042dd21804651b5d08529cbb1b519","metadata_url":""},"playerVisible":true,"playerType":"nosling","sourceType":"live_linear","channelInformation":{"callsign":"COOKHD","channel":"COOKHD","name":"COOKHD","image_url":"http://supair.dishanywhere.com/supair/20/b3/20b31799724ad6ae9d6bbc9558fa7065.png","service_unique_id":"COOKHD","ott_mapping_id":"COOK","linear_channel_name":"Cooking","major":"113","padded":"113-linear","satellite_callsign":"COOK","network_id":505}}`,
        // );
        const { sourceType = 'none' } = action;

        /* This is required by web sender in order to render proper player ui. */
        dispatch(setCastSourceType(sourceType || 'none'));

        const {
          cast: { storeHasBeenSynced, sourceType: prevSourceType },
          franchise: {
            franchiseSlug,
            franchiseDetails: { bySlug: franchiseDetailsBySlug },
          },
          player: { airings, resumePlayback },
          guide: { openAiring },
        } = state;

        const franchiseInfo = (franchiseSlug && franchiseDetailsBySlug[franchiseSlug]) || {};
        const recording = getRecordingByDvrId(dvrId, state);
        const airing = !isEmpty(airings) ? airings[0] : {};

        const castDetails: CastDetails = getCastDetails(
          sourceType,
          recording,
          openAiring,
          franchiseInfo,
          airing,
        );

        const { type: _, ...setPlayerStreamPayload } = action;

        const optimizedSource = optimizeSource(JSON.stringify(setPlayerStreamPayload));
        const source = isSlingSourceType(sourceType)
          ? { ...optimizedSource, ...getSlingConfig(state, action), slug: castDetails.slug }
          : { ...optimizedSource, slug: castDetails.slug };

        console.log('[CAST] Sending source:', source);
        const serializedStream = JSON.stringify(source);

        const wasPlayingSling = isSlingSourceType(prevSourceType);
        const willPlaySling = isSlingSourceType(sourceType);

        let seekTo;
        if (
          resumePlayback &&
          (sourceType === SourceType.ON_DEMAND || sourceType === SourceType.EST)
        ) {
          seekTo = await getCurrentContentTime(dispatch, franchiseInfo, stream);
        }

        startCast({
          state,
          dispatch,
          sourceType,
          castDetails,
          serializedStream,
          seekTo,
          syncFullStore: !storeHasBeenSynced,
          castType:
            wasPlayingSling && willPlaySling
              ? 'SEND_AUSTIN_CONFIGURE_REQUEST'
              : 'SEND_LOAD_MEDIA_REQUEST',
        });
        return; /* Root reducer shouldn't precess these actions in case of casting content. */
      }
      break;
    }
    case 'CAST_SESSION_STARTED': {
      const { getState, dispatch } = store;
      const state = getState();

      const {
        player: {
          playerType,
          stream: { drm: { widevine: drm } = {}, source: { dash } = {} } = {},
          playerVisible,
          dvrId,
          dvrProgress,
          sourceType = 'none',
          contentId,
          listing,
          channel,
          stream,
          channel: { channel: channelId, service_unique_id: serviceUniqueId } = {},
        },
        networks,
        cast: { storeHasBeenSynced, currentVideoProgress },
        app: { activeReceiver: { finderId, slingAdmin } = {} },
        franchise: {
          franchiseSlug,
          franchiseDetails: { bySlug: franchiseDetailsBySlug },
        },
        player: { channelInfo: channelInformation, airings },
        guide: { openAiring },
      } = state;

      /* Fetch networks to get networks banners (for casting sling content) */
      if (networks.content.length === 0) dispatch(fetchAllNetworks());

      const isPlaying = ((!!drm && !!dash) || playerType === 'sling') && playerVisible;

      let seekTo;
      if (sourceType === SourceType.ON_DEMAND || sourceType === SourceType.EST) {
        seekTo = currentVideoProgress;
      }
      if (isPlaying) {
        let source = {};
        if (sourceType === SourceType.SLING_DVR) {
          source = {
            dvrId,
            dvrProgress: currentVideoProgress,
            playerType,
            sourceType,
            contentId,
            finderId,
            slingAdmin,
          };
        } else if (sourceType === SourceType.SLING_LIVE) {
          source = {
            channel,
            playerType,
            sourceType,
            finderId,
            slingAdmin,
            channelId,
            serviceUniqueId,
          };
        } else {
          /* EST, LL, OD */
          source = { contentId, listing, playerType, sourceType, stream, channelInformation };
        }

        const recording = getRecordingByDvrId(dvrId, state);
        const franchiseInfo = (franchiseSlug && franchiseDetailsBySlug[franchiseSlug]) || {};
        const airing = !isEmpty(airings) ? airings[0] : {};

        const castDetails: CastDetails = getCastDetails(
          sourceType,
          recording,
          openAiring,
          franchiseInfo,
          airing,
        );
        source = { ...source, playerVisible: true, slug: castDetails.slug };

        const serializedStream = JSON.stringify(optimizeSource(JSON.stringify(source)));

        /* This is required by web sender in order to render proper player ui. */
        dispatch(setCastSourceType(sourceType));

        startCast({
          state,
          dispatch,
          sourceType,
          castDetails,
          serializedStream,
          seekTo,
          syncFullStore: !storeHasBeenSynced,
          castType: 'SEND_LOAD_MEDIA_REQUEST',
        });
        dispatch(resetPlayer(false));
      }
      break;
    }
    default:
      break;
  }
  next(action);
};
